// src/pages/IletisimPage.js
import React from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNotify } from "react-admin";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import ImageIcon from "@mui/icons-material/Image";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";
import PhoneIcon from "@mui/icons-material/Phone";
import PublicIcon from "@mui/icons-material/Public";
import SaveIcon from "@mui/icons-material/Save";
import TwitterIcon from "@mui/icons-material/Twitter";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { AnimatePresence, motion } from "framer-motion";
import ResimUpload from "../ResimUpload";
import { API_BASE } from "../config";

const COLORS = {
  navy: "#081a32",
  navySoft: "#12365f",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  cyan: "#0891b2",
  orange: "#f59e0b",
  orangeSoft: "#fff7ed",
  green: "#10b981",
  greenDark: "#047857",
  greenSoft: "#ecfdf5",
  red: "#ef4444",
  redSoft: "#fff1f2",
  text: "#0f172a",
  textSoft: "#64748b",
  page: "#f2f5fa",
  card: "#ffffff",
  border: "#e2e8f0",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "#fff",
    transition: "box-shadow .2s ease, border-color .2s ease",
    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(37,99,235,.09)",
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 650,
  },
};

// MUI sürümünden bağımsız, tam genişlikte ve dengeli kolon yapısı.
// Mevcut Grid alanları korunur; yalnızca yerleşim CSS Grid ile yönetilir.
const cssGrid = (columns, gap = 2) => ({
  display: "grid",
  gridTemplateColumns: columns,
  gap,
  width: "100%",
  maxWidth: "none",
  minWidth: 0,
  margin: "0 !important",
  alignItems: "stretch",
  "& > .MuiGrid-root": {
    width: "100% !important",
    maxWidth: "none !important",
    minWidth: "0 !important",
    padding: "0 !important",
  },
  "& > .MuiGrid-root > *": {
    width: "100% !important",
    minWidth: "0 !important",
  },
});

const ALLOWED = ["tr", "en", "de"];

const bust = (url, version) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${version}` : "";

const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

const sectionMotion = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.36, ease: "easeOut" },
};

const T = {
  tr: {
    pageTitle: "İletişim Merkezi",
    pageSubtitle:
      "Kurumunuzun iletişim, adres, sosyal medya, banka ve harita bilgilerini yönetin.",
    save: "Tüm Değişiklikleri Kaydet",
    saving: "Kaydediliyor…",
    savedOk: "✅ İletişim bilgileri kaydedildi",
    savedErr: "❌ Kayıt hatası",
    loading: "İletişim bilgileri yükleniyor…",
    newSuffix: "Yeni Kayıt",
    editSuffix: "Düzenleme",
    general: "Kurumsal Kimlik",
    generalDesc: "Kurum adı, logo ve genel yayın durumu",
    addressHours: "Adres ve Çalışma Saatleri",
    addressDesc: "Aktif içerik dillerine göre adres ve çalışma saatleri",
    contact: "İletişim Kanalları",
    contactDesc: "Telefon, WhatsApp ve e-posta bilgileriniz",
    social: "Sosyal Medya ve Web",
    socialDesc: "Kurumsal web sitesi ve sosyal medya hesapları",
    finance: "Bağış ve Finans Bilgileri",
    financeDesc: "Kurumunuza ait aktif banka ve IBAN kayıtları",
    map: "Harita ve Konum",
    mapDesc: "Google Maps bağlantısı ve konum önizlemesi",
    name: "Kurum Adı",
    logo: "Logo / Kurumsal Görsel",
    logoUrl: "Logo URL",
    allActive: "İletişim kartını yayınla",
    active: "Aktif",
    passive: "Pasif",
    open: "Açık",
    closed: "Kapalı",
    add: "Ekle",
    remove: "Sil",
    preview: "Canlı Önizleme",
    addressLabel: (code) => `Adres (${code.toUpperCase()})`,
    hoursLabel: (code) => `Çalışma Saatleri (${code.toUpperCase()})`,
    phone: "Telefon",
    whatsapp: "WhatsApp",
    email: "E-posta",
    instagram: "Instagram",
    website: "Web Sitesi",
    facebook: "Facebook",
    twitter: "X (Twitter)",
    linkedin: "LinkedIn",
    youtube: "YouTube",
    addIban: "Yeni IBAN Ekle",
    bankName: "Banka Adı",
    accountName: "Hesap Adı / Açıklama",
    iban: "IBAN",
    bankAccount: "Banka Hesabı",
    noIban: "Henüz IBAN bilgisi eklenmedi.",
    mapsUrl: "Google Maps URL",
    mapsPlaceholder: "https://maps.google.com/?q=...",
    activeChannels: "Aktif Kanal",
    ibanCount: "IBAN Kaydı",
    languageCount: "Aktif Dil",
    cardStatus: "Kart Durumu",
    copied: "IBAN panoya kopyalandı.",
  },

  en: {
    pageTitle: "Contact Center",
    pageSubtitle:
      "Manage your organization’s contact, address, social, banking and map information.",
    save: "Save All Changes",
    saving: "Saving…",
    savedOk: "✅ Contact information saved",
    savedErr: "❌ Save error",
    loading: "Loading contact information…",
    newSuffix: "New Record",
    editSuffix: "Editing",
    general: "Corporate Identity",
    generalDesc: "Organization name, logo and overall publishing status",
    addressHours: "Address and Opening Hours",
    addressDesc: "Address and hours for active content languages",
    contact: "Contact Channels",
    contactDesc: "Phone, WhatsApp and email information",
    social: "Social Media and Web",
    socialDesc: "Corporate website and social media accounts",
    finance: "Donations and Finance",
    financeDesc: "Active bank and IBAN records for your organization",
    map: "Map and Location",
    mapDesc: "Google Maps link and location preview",
    name: "Organization Name",
    logo: "Logo / Corporate Image",
    logoUrl: "Logo URL",
    allActive: "Publish contact card",
    active: "Active",
    passive: "Inactive",
    open: "On",
    closed: "Off",
    add: "Add",
    remove: "Delete",
    preview: "Live Preview",
    addressLabel: (code) => `Address (${code.toUpperCase()})`,
    hoursLabel: (code) => `Opening Hours (${code.toUpperCase()})`,
    phone: "Phone",
    whatsapp: "WhatsApp",
    email: "Email",
    instagram: "Instagram",
    website: "Website",
    facebook: "Facebook",
    twitter: "X (Twitter)",
    linkedin: "LinkedIn",
    youtube: "YouTube",
    addIban: "Add New IBAN",
    bankName: "Bank Name",
    accountName: "Account Name / Note",
    iban: "IBAN",
    bankAccount: "Bank Account",
    noIban: "No IBAN information has been added yet.",
    mapsUrl: "Google Maps URL",
    mapsPlaceholder: "https://maps.google.com/?q=...",
    activeChannels: "Active Channels",
    ibanCount: "IBAN Records",
    languageCount: "Active Languages",
    cardStatus: "Card Status",
    copied: "IBAN copied to clipboard.",
  },

  de: {
    pageTitle: "Kontaktzentrum",
    pageSubtitle:
      "Verwalten Sie Kontakt-, Adress-, Social-Media-, Bank- und Karteninformationen.",
    save: "Alle Änderungen speichern",
    saving: "Wird gespeichert…",
    savedOk: "✅ Kontaktinformationen gespeichert",
    savedErr: "❌ Fehler beim Speichern",
    loading: "Kontaktinformationen werden geladen…",
    newSuffix: "Neuer Eintrag",
    editSuffix: "Bearbeitung",
    general: "Unternehmensidentität",
    generalDesc: "Organisationsname, Logo und Veröffentlichungsstatus",
    addressHours: "Adresse und Öffnungszeiten",
    addressDesc: "Adresse und Zeiten für aktive Inhaltssprachen",
    contact: "Kontaktkanäle",
    contactDesc: "Telefon-, WhatsApp- und E-Mail-Informationen",
    social: "Soziale Medien und Web",
    socialDesc: "Webseite und Social-Media-Konten",
    finance: "Spenden und Finanzen",
    financeDesc: "Aktive Bank- und IBAN-Einträge Ihrer Organisation",
    map: "Karte und Standort",
    mapDesc: "Google-Maps-Link und Standortvorschau",
    name: "Name der Organisation",
    logo: "Logo / Unternehmensbild",
    logoUrl: "Logo-URL",
    allActive: "Kontaktkarte veröffentlichen",
    active: "Aktiv",
    passive: "Inaktiv",
    open: "Ein",
    closed: "Aus",
    add: "Hinzufügen",
    remove: "Löschen",
    preview: "Live-Vorschau",
    addressLabel: (code) => `Adresse (${code.toUpperCase()})`,
    hoursLabel: (code) => `Öffnungszeiten (${code.toUpperCase()})`,
    phone: "Telefon",
    whatsapp: "WhatsApp",
    email: "E-Mail",
    instagram: "Instagram",
    website: "Webseite",
    facebook: "Facebook",
    twitter: "X (Twitter)",
    linkedin: "LinkedIn",
    youtube: "YouTube",
    addIban: "Neue IBAN hinzufügen",
    bankName: "Bankname",
    accountName: "Kontoname / Notiz",
    iban: "IBAN",
    bankAccount: "Bankkonto",
    noIban: "Noch keine IBAN-Informationen hinzugefügt.",
    mapsUrl: "Google Maps URL",
    mapsPlaceholder: "https://maps.google.com/?q=...",
    activeChannels: "Aktive Kanäle",
    ibanCount: "IBAN-Einträge",
    languageCount: "Aktive Sprachen",
    cardStatus: "Kartenstatus",
    copied: "IBAN wurde kopiert.",
  },
};

function useUiTexts() {
  const [uiLang, setUiLang] = React.useState(() =>
    (localStorage.getItem("app_lang") || "tr").slice(0, 2).toLowerCase(),
  );

  React.useEffect(() => {
    const sync = () => {
      const nextLang = (localStorage.getItem("app_lang") || "tr")
        .slice(0, 2)
        .toLowerCase();

      setUiLang(ALLOWED.includes(nextLang) ? nextLang : "tr");
    };

    window.addEventListener("app_lang_change", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener("app_lang_change", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  return T[ALLOWED.includes(uiLang) ? uiLang : "tr"];
}

function StatusSwitch({ label, checked, onChange, TT, compact = false }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: compact ? 1 : 1.5,
        py: compact ? 0.45 : 0.75,
        borderRadius: 2.5,
        borderColor: checked ? "#a7f3d0" : COLORS.border,
        bgcolor: checked ? COLORS.greenSoft : "#f8fafc",
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Stack direction="row" spacing={0.7} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: checked ? COLORS.green : "#94a3b8",
              flexShrink: 0,
            }}
          />

          <Typography
            variant="body2"
            sx={{
              fontWeight: 800,
              color: checked ? COLORS.greenDark : COLORS.textSoft,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
        </Stack>

        <Switch
          size={compact ? "small" : "medium"}
          checked={!!checked}
          onChange={(event) => onChange(event.target.checked)}
        />
      </Stack>
    </Paper>
  );
}

function SectionHeader({
  icon,
  title,
  description,
  action,
  accent = COLORS.blue,
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
      justifyContent="space-between"
      spacing={1.5}
      sx={{ mb: 2.5 }}
    >
      <Stack direction="row" spacing={1.35} alignItems="center" minWidth={0}>
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: 2.75,
            display: "grid",
            placeItems: "center",
            bgcolor: `${accent}12`,
            color: accent,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box minWidth={0}>
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.05rem", sm: "1.2rem" },
              fontWeight: 950,
              color: COLORS.text,
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.35,
                color: COLORS.textSoft,
                lineHeight: 1.5,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Stack>

      {action && <Box flexShrink={0}>{action}</Box>}
    </Stack>
  );
}

function AnimatedSection({ children, delay = 0, ...props }) {
  return (
    <MotionPaper
      {...sectionMotion}
      transition={{
        ...sectionMotion.transition,
        delay,
      }}
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        p: { xs: 1.75, sm: 2.5, lg: 3 },
        borderRadius: { xs: 3, md: 4 },
        borderColor: COLORS.border,
        bgcolor: COLORS.card,
        boxShadow: "0 14px 38px rgba(15,23,42,.055)",
        overflow: "hidden",
        ...(props.sx || {}),
      }}
    >
      {children}
    </MotionPaper>
  );
}

function MetricCard({ icon, label, value, accent }) {
  return (
    <MotionPaper
      whileHover={{ y: -3 }}
      transition={{
        type: "spring",
        stiffness: 220,
        damping: 20,
      }}
      sx={{
        width: "100%",
        minHeight: 104,
        p: { xs: 1.4, sm: 1.75 },
        borderRadius: 3,
        bgcolor: "rgba(255,255,255,.1)",
        border: "1px solid rgba(255,255,255,.15)",
        backdropFilter: "blur(10px)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ color: accent }}>{icon}</Box>

      <Box>
        <Typography
          sx={{
            fontSize: "1.55rem",
            lineHeight: 1,
            fontWeight: 950,
          }}
        >
          {value}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            mt: 0.6,
            display: "block",
            color: "rgba(255,255,255,.72)",
            fontWeight: 750,
          }}
        >
          {label}
        </Typography>
      </Box>
    </MotionPaper>
  );
}

function ChannelCard({
  icon,
  label,
  value,
  active,
  onValueChange,
  onActiveChange,
  placeholder,
  accent,
  TT,
}) {
  return (
    <MotionPaper
      whileHover={{
        y: -3,
        boxShadow: "0 15px 34px rgba(15,23,42,.09)",
      }}
      transition={{
        type: "spring",
        stiffness: 220,
        damping: 22,
      }}
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        p: 1.75,
        borderRadius: 3,
        borderColor: active ? `${accent}45` : COLORS.border,
        bgcolor: active ? `${accent}08` : "#fff",
        height: "100%",
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2.25,
                display: "grid",
                placeItems: "center",
                bgcolor: `${accent}14`,
                color: accent,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>

            <Typography
              sx={{
                fontWeight: 900,
                color: COLORS.text,
              }}
            >
              {label}
            </Typography>
          </Stack>

          <Chip
            size="small"
            label={active ? TT.active : TT.passive}
            sx={{
              fontWeight: 800,
              color: active ? COLORS.greenDark : COLORS.textSoft,
              bgcolor: active ? COLORS.greenSoft : "#f1f5f9",
            }}
          />
        </Stack>

        <TextField
          fullWidth
          size="small"
          label={label}
          value={value || ""}
          placeholder={placeholder || ""}
          onChange={(event) => onValueChange(event.target.value)}
          sx={fieldSx}
        />

        <FormControlLabel
          control={
            <Switch
              checked={!!active}
              onChange={(event) => onActiveChange(event.target.checked)}
            />
          }
          label={active ? TT.open : TT.closed}
          sx={{
            m: 0,
            "& .MuiFormControlLabel-label": {
              fontWeight: 750,
              fontSize: ".82rem",
              color: active ? COLORS.greenDark : COLORS.textSoft,
            },
          }}
        />
      </Stack>
    </MotionPaper>
  );
}

function IbanItem({ item, index, onChange, onRemove, TT, onCopy }) {
  return (
    <MotionPaper
      layout
      initial={{
        opacity: 0,
        y: 12,
        scale: 0.98,
      }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      exit={{
        opacity: 0,
        x: -30,
        scale: 0.97,
      }}
      transition={{ duration: 0.25 }}
      variant="outlined"
      sx={{
        width: "100%",
        minWidth: 0,
        p: { xs: 1.5, sm: 2 },
        borderRadius: 3,
        borderColor: item.aktif ? "#a7f3d0" : COLORS.border,
        bgcolor: item.aktif ? "#fbfffd" : "#f8fafc",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.25,
              display: "grid",
              placeItems: "center",
              bgcolor: COLORS.blueSoft,
              color: COLORS.blue,
            }}
          >
            <AccountBalanceIcon fontSize="small" />
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                color: COLORS.text,
              }}
            >
              {TT.bankAccount} {String(index + 1).padStart(2, "0")}
            </Typography>

            <Typography variant="caption" sx={{ color: COLORS.textSoft }}>
              {item.banka_adi || TT.bankName}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={!!item.aktif}
                onChange={(event) =>
                  onChange({
                    ...item,
                    aktif: event.target.checked,
                  })
                }
              />
            }
            label={item.aktif ? TT.active : TT.passive}
            sx={{
              m: 0,
              "& .MuiFormControlLabel-label": {
                fontWeight: 800,
                color: item.aktif ? COLORS.greenDark : COLORS.textSoft,
              },
            }}
          />

          <Tooltip title={TT.remove}>
            <IconButton
              color="error"
              onClick={onRemove}
              sx={{
                bgcolor: COLORS.redSoft,
                "&:hover": {
                  bgcolor: "#ffe4e6",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Grid
        container
        spacing={0}
        sx={cssGrid(
          {
            xs: "minmax(0, 1fr)",
            md: "minmax(0, 3fr) minmax(0, 3fr) minmax(0, 6fr)",
          },
          1.5,
        )}
      >
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label={TT.bankName}
            value={item.banka_adi || ""}
            onChange={(event) =>
              onChange({
                ...item,
                banka_adi: event.target.value,
              })
            }
            sx={fieldSx}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label={TT.accountName}
            value={item.hesap_adi || ""}
            onChange={(event) =>
              onChange({
                ...item,
                hesap_adi: event.target.value,
              })
            }
            sx={fieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label={TT.iban}
            value={item.iban || ""}
            onChange={(event) =>
              onChange({
                ...item,
                iban: event.target.value,
              })
            }
            InputProps={{
              endAdornment: item.iban ? (
                <InputAdornment position="end">
                  <Tooltip title={TT.copied}>
                    <IconButton edge="end" onClick={() => onCopy(item.iban)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
            sx={fieldSx}
          />
        </Grid>
      </Grid>
    </MotionPaper>
  );
}

function SharedIletisimForm({ mode = "list" }) {
  const TT = useUiTexts();
  const notify = useNotify();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [id, setId] = React.useState(null);
  const [version, setVersion] = React.useState(0);
  const [aktifDiller, setAktifDiller] = React.useState(ALLOWED);
  const [copySnack, setCopySnack] = React.useState(false);

  const [form, setForm] = React.useState({
    kart_aktif: true,

    kurum_adi: "",
    kurum_adi_aktif: true,

    logo_url: "",
    logo_aktif: true,

    adres_tr: "",
    adres_tr_aktif: true,
    saat_tr: "",
    saat_tr_aktif: true,

    adres_en: "",
    adres_en_aktif: true,
    saat_en: "",
    saat_en_aktif: true,

    adres_de: "",
    adres_de_aktif: true,
    saat_de: "",
    saat_de_aktif: true,

    telefon: "",
    telefon_aktif: true,

    whatsapp: "",
    whatsapp_aktif: true,

    email: "",
    email_aktif: true,

    instagram: "",
    instagram_aktif: true,

    website: "",
    website_aktif: true,

    facebook: "",
    facebook_aktif: false,

    twitter: "",
    twitter_aktif: false,

    linkedin: "",
    linkedin_aktif: false,

    youtube: "",
    youtube_aktif: false,

    ibans: [],

    harita_url: "",
    harita_aktif: true,
  });

  const set = (patch) => {
    setForm((previous) => ({
      ...previous,
      ...patch,
    }));
  };

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem("kurum_ayarlar");

      if (raw) {
        const settings = JSON.parse(raw);

        const languages = Array.isArray(settings?.aktif_diller)
          ? settings.aktif_diller.map((item) => item?.toLowerCase().slice(0, 2))
          : ALLOWED;

        const filtered = languages.filter((code) => ALLOWED.includes(code));

        setAktifDiller(filtered.length ? filtered : ALLOWED);
      } else {
        setAktifDiller(ALLOWED);
      }
    } catch {
      setAktifDiller(ALLOWED);
    }
  }, []);

  React.useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("token");

        const response = await fetch(`${API_BASE}/iletisim`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const json = await response.json();

          if (json && Object.keys(json).length) {
            setId(json.id);

            setForm((previous) => ({
              ...previous,
              ...Object.fromEntries(
                Object.entries(json).map(([key, value]) => [
                  key,
                  value == null ? "" : value,
                ]),
              ),
              ibans: Array.isArray(json.ibans)
                ? json.ibans.map((item) => ({
                    ...item,
                    aktif: !!item.aktif,
                  }))
                : [],
            }));

            setVersion((current) => current + 1);
          }
        }
      } catch {
        // Mevcut davranış korunur.
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem("token");

      const payload = {
        ...form,
        ibans: Array.isArray(form.ibans) ? form.ibans : [],
      };

      let response;

      if (id) {
        response = await fetch(`${API_BASE}/iletisim/${id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE}/iletisim`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        const json = await response.json().catch(() => ({}));

        if (json?.id) {
          setId(json.id);
        }

        notify(TT.savedOk, {
          type: "success",
        });
      } else {
        notify(TT.savedErr, {
          type: "warning",
        });
      }
    } catch {
      notify(TT.savedErr, {
        type: "warning",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyIban = async (iban) => {
    try {
      await navigator.clipboard?.writeText(iban);
      setCopySnack(true);
    } catch {
      // Kopyalama izni yoksa sessizce devam eder.
    }
  };

  const addressKey = (code) => `adres_${code}`;
  const hoursKey = (code) => `saat_${code}`;

  const addressActiveKey = (code) => `adres_${code}_aktif`;

  const hoursActiveKey = (code) => `saat_${code}_aktif`;

  const contactChannels = [
    {
      key: "telefon",
      label: TT.phone,
      icon: <PhoneIcon fontSize="small" />,
      accent: "#2563eb",
      placeholder: "+41 ...",
    },
    {
      key: "whatsapp",
      label: TT.whatsapp,
      icon: <WhatsAppIcon fontSize="small" />,
      accent: "#16a34a",
      placeholder: "+41 ...",
    },
    {
      key: "email",
      label: TT.email,
      icon: <EmailIcon fontSize="small" />,
      accent: "#ea580c",
      placeholder: "info@kurum.com",
    },
  ];

  const socialChannels = [
    {
      key: "instagram",
      label: TT.instagram,
      icon: <InstagramIcon fontSize="small" />,
      accent: "#c026d3",
      placeholder: "https://instagram.com/...",
    },
    {
      key: "website",
      label: TT.website,
      icon: <PublicIcon fontSize="small" />,
      accent: "#0284c7",
      placeholder: "https://...",
    },
    {
      key: "facebook",
      label: TT.facebook,
      icon: <FacebookIcon fontSize="small" />,
      accent: "#2563eb",
      placeholder: "https://facebook.com/...",
    },
    {
      key: "twitter",
      label: TT.twitter,
      icon: <TwitterIcon fontSize="small" />,
      accent: "#0f172a",
      placeholder: "https://x.com/...",
    },
    {
      key: "linkedin",
      label: TT.linkedin,
      icon: <LinkedInIcon fontSize="small" />,
      accent: "#0369a1",
      placeholder: "https://linkedin.com/...",
    },
    {
      key: "youtube",
      label: TT.youtube,
      icon: <YouTubeIcon fontSize="small" />,
      accent: "#dc2626",
      placeholder: "https://youtube.com/...",
    },
  ];

  const activeChannelCount = [...contactChannels, ...socialChannels].filter(
    ({ key }) => !!form[`${key}_aktif`],
  ).length;

  const titleSuffix =
    mode === "create" ? TT.newSuffix : mode === "edit" ? TT.editSuffix : "";

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "grid",
          placeItems: "center",
          bgcolor: COLORS.page,
        }}
      >
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress />

          <Typography
            sx={{
              color: COLORS.textSoft,
              fontWeight: 700,
            }}
          >
            {TT.loading}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          html: {
            overflowX: "hidden",
          },
          body: {
            margin: 0,
            overflowX: "hidden",
            background: COLORS.page,
          },
          "*": {
            boxSizing: "border-box",
          },
          ".MuiSnackbarContent-message": {
            fontSize: "1rem",
            fontWeight: 800,
          },
          ".RaNotification-content": {
            fontSize: "1rem",
            fontWeight: 800,
          },
        }}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "hidden",
          bgcolor: COLORS.page,
          pb: 4,
        }}
      >
        <Box
          sx={{
            width: "100%",
            color: "#fff",
            background:
              "linear-gradient(125deg, #06172d 0%, #123d70 52%, #075985 100%)",
            borderBottom: `4px solid ${COLORS.orange}`,
            position: "relative",
            overflow: "hidden",

            "&::before": {
              content: '""',
              position: "absolute",
              width: { xs: 240, md: 460 },
              height: { xs: 240, md: 460 },
              borderRadius: "50%",
              right: { xs: -145, md: -160 },
              top: { xs: -120, md: -250 },
              background:
                "radial-gradient(circle, rgba(255,255,255,.16), transparent 68%)",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              px: {
                xs: 1.5,
                sm: 2.5,
                md: 3.5,
              },
              pt: {
                xs: 3,
                md: 4.5,
              },
              pb: {
                xs: 3.5,
                md: 4.5,
              },
              position: "relative",
              zIndex: 1,
            }}
          >
            <Stack
              direction={{
                xs: "column",
                lg: "row",
              }}
              alignItems={{
                xs: "stretch",
                lg: "flex-end",
              }}
              justifyContent="space-between"
              spacing={3}
            >
              <Box sx={{ maxWidth: 820 }}>
                {titleSuffix && (
                  <Chip
                    label={titleSuffix}
                    sx={{
                      mb: 1.5,
                      color: "#fff",
                      fontWeight: 800,
                      bgcolor: "rgba(255,255,255,.12)",
                      border: "1px solid rgba(255,255,255,.18)",
                    }}
                  />
                )}

                <Typography
                  component="h1"
                  sx={{
                    fontSize: {
                      xs: "1.9rem",
                      sm: "2.6rem",
                      md: "3.25rem",
                    },
                    lineHeight: 1.05,
                    fontWeight: 950,
                    letterSpacing: {
                      xs: -0.5,
                      md: -1.2,
                    },
                  }}
                >
                  {TT.pageTitle}
                </Typography>

                <Typography
                  sx={{
                    mt: 1.2,
                    color: "rgba(255,255,255,.76)",
                    lineHeight: 1.65,
                    fontSize: {
                      xs: ".94rem",
                      sm: "1.05rem",
                    },
                  }}
                >
                  {TT.pageSubtitle}
                </Typography>
              </Box>

              <StatusSwitch
                label={TT.allActive}
                checked={form.kart_aktif}
                onChange={(value) => set({ kart_aktif: value })}
                TT={TT}
              />
            </Stack>

            <Grid
              container
              spacing={0}
              sx={{
                ...cssGrid(
                  {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  { xs: 1, sm: 1.25 },
                ),
                mt: 2.5,
              }}
            >
              <Grid item xs={6} md={3} sx={{ display: "flex" }}>
                <MetricCard
                  icon={<PhoneIcon />}
                  label={TT.activeChannels}
                  value={activeChannelCount}
                  accent="#67e8f9"
                />
              </Grid>

              <Grid item xs={6} md={3} sx={{ display: "flex" }}>
                <MetricCard
                  icon={<AccountBalanceIcon />}
                  label={TT.ibanCount}
                  value={(form.ibans || []).length}
                  accent="#fbbf24"
                />
              </Grid>

              <Grid item xs={6} md={3} sx={{ display: "flex" }}>
                <MetricCard
                  icon={<LanguageIcon />}
                  label={TT.languageCount}
                  value={aktifDiller.length}
                  accent="#86efac"
                />
              </Grid>

              <Grid item xs={6} md={3} sx={{ display: "flex" }}>
                <MetricCard
                  icon={<CheckCircleIcon />}
                  label={TT.cardStatus}
                  value={form.kart_aktif ? TT.active : TT.passive}
                  accent={form.kart_aktif ? "#86efac" : "#cbd5e1"}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box
          component="form"
          onSubmit={save}
          sx={{
            width: "100%",
            px: {
              xs: 1,
              sm: 2,
              md: 3,
            },
            mt: {
              xs: 1.5,
              md: 2.5,
            },
          }}
        >
          <Stack
            spacing={{
              xs: 1.5,
              md: 2.25,
            }}
          >
            <AnimatedSection delay={0.03}>
              <SectionHeader
                icon={<BusinessIcon />}
                title={TT.general}
                description={TT.generalDesc}
                accent={COLORS.blue}
              />

              <Grid
                container
                spacing={0}
                alignItems="stretch"
                sx={cssGrid(
                  {
                    xs: "minmax(0, 1fr)",
                    lg: "minmax(0, 7fr) minmax(0, 5fr)",
                  },
                  2,
                )}
              >
                <Grid item xs={12} lg={7}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: {
                        xs: 1.5,
                        sm: 2,
                      },
                      borderRadius: 3,
                      borderColor: COLORS.border,
                      height: "100%",
                    }}
                  >
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        label={TT.name}
                        value={form.kurum_adi || ""}
                        onChange={(event) =>
                          set({
                            kurum_adi: event.target.value,
                          })
                        }
                        sx={fieldSx}
                      />

                      <StatusSwitch
                        label={TT.name}
                        checked={form.kurum_adi_aktif}
                        onChange={(value) =>
                          set({
                            kurum_adi_aktif: value,
                          })
                        }
                        TT={TT}
                        compact
                      />
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={5}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: {
                        xs: 1.5,
                        sm: 2,
                      },
                      borderRadius: 3,
                      borderColor: COLORS.border,
                      height: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      direction={{
                        xs: "column",
                        sm: "row",
                      }}
                      spacing={1.75}
                      alignItems={{
                        xs: "stretch",
                        sm: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: {
                            xs: 96,
                            sm: 112,
                          },
                          height: {
                            xs: 96,
                            sm: 112,
                          },
                          alignSelf: {
                            xs: "center",
                            sm: "auto",
                          },
                          borderRadius: 3,
                          overflow: "hidden",
                          bgcolor: "#e2e8f0",
                          border: "3px solid #fff",
                          boxShadow: "0 8px 22px rgba(15,23,42,.12)",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        {form.logo_url ? (
                          <Box
                            component="img"
                            src={bust(form.logo_url, version)}
                            alt={form.kurum_adi || TT.logo}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <ImageIcon
                            sx={{
                              fontSize: 42,
                              color: "#94a3b8",
                            }}
                          />
                        )}
                      </Box>

                      <Stack
                        spacing={1.25}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={TT.logoUrl}
                          value={form.logo_url || ""}
                          onChange={(event) =>
                            set({
                              logo_url: event.target.value,
                            })
                          }
                          sx={fieldSx}
                        />

                        <Box
                          sx={{
                            maxWidth: "100%",
                            overflow: "hidden",
                            "& button": {
                              maxWidth: "100%",
                            },
                          }}
                        >
                          <ResimUpload
                            onUpload={(url) => {
                              set({
                                logo_url: url,
                              });

                              setVersion((current) => current + 1);
                            }}
                            defaultPreview={bust(form.logo_url, version)}
                            label={TT.logo}
                          />
                        </Box>

                        <StatusSwitch
                          label={TT.logo}
                          checked={form.logo_aktif}
                          onChange={(value) =>
                            set({
                              logo_aktif: value,
                            })
                          }
                          TT={TT}
                          compact
                        />
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </AnimatedSection>

            <AnimatedSection delay={0.06}>
              <SectionHeader
                icon={<LocationOnIcon />}
                title={TT.addressHours}
                description={TT.addressDesc}
                accent={COLORS.orange}
              />

              <Grid
                container
                spacing={0}
                alignItems="stretch"
                sx={cssGrid(
                  {
                    xs: "minmax(0, 1fr)",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                  },
                  1.75,
                )}
              >
                {aktifDiller.map((code) => {
                  const addrKey = addressKey(code);

                  const hourKey = hoursKey(code);

                  const addrActive = addressActiveKey(code);

                  const hourActive = hoursActiveKey(code);

                  const combinedActive = !!(
                    form[addrActive] && form[hourActive]
                  );

                  return (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      xl={4}
                      key={code}
                      sx={{
                        display: "flex",
                      }}
                    >
                      <MotionPaper
                        whileHover={{ y: -3 }}
                        transition={{
                          type: "spring",
                          stiffness: 220,
                          damping: 22,
                        }}
                        variant="outlined"
                        sx={{
                          width: "100%",
                          p: {
                            xs: 1.5,
                            sm: 2,
                          },
                          borderRadius: 3,
                          borderColor: combinedActive
                            ? "#fed7aa"
                            : COLORS.border,
                          bgcolor: combinedActive ? "#fffcf8" : "#f8fafc",
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: 1.5 }}
                        >
                          <Chip
                            icon={<LanguageIcon />}
                            label={code.toUpperCase()}
                            sx={{
                              fontWeight: 900,
                              color: "#9a3412",
                              bgcolor: COLORS.orangeSoft,
                              "& .MuiChip-icon": {
                                color: COLORS.orange,
                              },
                            }}
                          />

                          <Switch
                            checked={combinedActive}
                            onChange={(event) =>
                              set({
                                [addrActive]: event.target.checked,
                                [hourActive]: event.target.checked,
                              })
                            }
                          />
                        </Stack>

                        <Stack spacing={1.5}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={TT.addressLabel(code)}
                            value={form[addrKey] || ""}
                            onChange={(event) =>
                              set({
                                [addrKey]: event.target.value,
                              })
                            }
                            sx={fieldSx}
                          />

                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={TT.hoursLabel(code)}
                            value={form[hourKey] || ""}
                            onChange={(event) =>
                              set({
                                [hourKey]: event.target.value,
                              })
                            }
                            sx={fieldSx}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <AccessTimeIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Stack>
                      </MotionPaper>
                    </Grid>
                  );
                })}
              </Grid>
            </AnimatedSection>

            <AnimatedSection delay={0.09}>
              <SectionHeader
                icon={<PhoneIcon />}
                title={TT.contact}
                description={TT.contactDesc}
                accent={COLORS.cyan}
              />

              <Grid
                container
                spacing={0}
                alignItems="stretch"
                sx={cssGrid(
                  {
                    xs: "minmax(0, 1fr)",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  1.5,
                )}
              >
                {contactChannels.map((channel) => (
                  <Grid
                    item
                    xs={12}
                    md={4}
                    key={channel.key}
                    sx={{ display: "flex" }}
                  >
                    <ChannelCard
                      {...channel}
                      value={form[channel.key]}
                      active={form[`${channel.key}_aktif`]}
                      onValueChange={(value) =>
                        set({
                          [channel.key]: value,
                        })
                      }
                      onActiveChange={(value) =>
                        set({
                          [`${channel.key}_aktif`]: value,
                        })
                      }
                      TT={TT}
                    />
                  </Grid>
                ))}
              </Grid>
            </AnimatedSection>

            <AnimatedSection delay={0.12}>
              <SectionHeader
                icon={<PublicIcon />}
                title={TT.social}
                description={TT.socialDesc}
                accent="#7c3aed"
              />

              <Grid
                container
                spacing={0}
                alignItems="stretch"
                sx={cssGrid(
                  {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                  },
                  1.5,
                )}
              >
                {socialChannels.map((channel) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    xl={4}
                    key={channel.key}
                    sx={{ display: "flex" }}
                  >
                    <ChannelCard
                      {...channel}
                      value={form[channel.key]}
                      active={form[`${channel.key}_aktif`]}
                      onValueChange={(value) =>
                        set({
                          [channel.key]: value,
                        })
                      }
                      onActiveChange={(value) =>
                        set({
                          [`${channel.key}_aktif`]: value,
                        })
                      }
                      TT={TT}
                    />
                  </Grid>
                ))}
              </Grid>
            </AnimatedSection>

            <AnimatedSection delay={0.15}>
              <SectionHeader
                icon={<AccountBalanceIcon />}
                title={TT.finance}
                description={TT.financeDesc}
                accent={COLORS.greenDark}
                action={
                  <Button
                    type="button"
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={() =>
                      set({
                        ibans: [
                          ...(form.ibans || []),
                          {
                            banka_adi: "",
                            hesap_adi: "",
                            iban: "",
                            aktif: true,
                          },
                        ],
                      })
                    }
                    sx={{
                      minHeight: 42,
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 850,
                      bgcolor: COLORS.greenDark,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#065f46",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {TT.addIban}
                  </Button>
                }
              />

              {(form.ibans || []).length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    py: 4,
                    px: 2,
                    borderRadius: 3,
                    borderStyle: "dashed",
                    borderColor: "#cbd5e1",
                    bgcolor: "#f8fafc",
                    textAlign: "center",
                  }}
                >
                  <AccountBalanceIcon
                    sx={{
                      fontSize: 42,
                      color: "#94a3b8",
                    }}
                  />

                  <Typography
                    sx={{
                      mt: 1,
                      color: COLORS.textSoft,
                      fontWeight: 750,
                    }}
                  >
                    {TT.noIban}
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={1.5}>
                  <AnimatePresence initial={false}>
                    {(form.ibans || []).map((item, index) => (
                      <IbanItem
                        key={`iban-${index}`}
                        item={item}
                        index={index}
                        TT={TT}
                        onCopy={copyIban}
                        onChange={(updated) => {
                          const items = [...(form.ibans || [])];

                          items[index] = updated;

                          set({
                            ibans: items,
                          });
                        }}
                        onRemove={() => {
                          const items = [...(form.ibans || [])];

                          items.splice(index, 1);

                          set({
                            ibans: items,
                          });
                        }}
                      />
                    ))}
                  </AnimatePresence>
                </Stack>
              )}
            </AnimatedSection>

            <AnimatedSection delay={0.18}>
              <SectionHeader
                icon={<MapIcon />}
                title={TT.map}
                description={TT.mapDesc}
                accent={COLORS.red}
                action={
                  <StatusSwitch
                    label={TT.active}
                    checked={form.harita_aktif}
                    onChange={(value) =>
                      set({
                        harita_aktif: value,
                      })
                    }
                    TT={TT}
                    compact
                  />
                }
              />

              <TextField
                fullWidth
                label={TT.mapsUrl}
                value={form.harita_url || ""}
                placeholder={TT.mapsPlaceholder}
                onChange={(event) =>
                  set({
                    harita_url: event.target.value,
                  })
                }
                sx={fieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon color="error" />
                    </InputAdornment>
                  ),
                }}
              />

              {form.harita_url && (
                <MotionBox
                  initial={{
                    opacity: 0,
                    height: 0,
                  }}
                  animate={{
                    opacity: 1,
                    height: "auto",
                  }}
                  transition={{
                    duration: 0.35,
                  }}
                  sx={{
                    mt: 2,
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 0.75,
                      display: "block",
                      color: COLORS.textSoft,
                      fontWeight: 750,
                    }}
                  >
                    {TT.preview}
                  </Typography>

                  <Box
                    component="iframe"
                    title="map"
                    src={form.harita_url}
                    loading="lazy"
                    sx={{
                      width: "100%",
                      height: {
                        xs: 260,
                        md: 360,
                      },
                      display: "block",
                      border: 0,
                      borderRadius: 3,
                      bgcolor: "#e2e8f0",
                    }}
                  />
                </MotionBox>
              )}
            </AnimatedSection>

            <Paper
              elevation={8}
              sx={{
                position: "sticky",
                bottom: {
                  xs: 8,
                  md: 12,
                },
                zIndex: 20,
                p: {
                  xs: 1.15,
                  sm: 1.5,
                },
                borderRadius: 3,
                border: `1px solid ${COLORS.border}`,
                background: "rgba(255,255,255,.94)",
                backdropFilter: "blur(14px)",
              }}
            >
              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                alignItems={{
                  xs: "stretch",
                  sm: "center",
                }}
                justifyContent="space-between"
                spacing={1.25}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon
                    sx={{
                      color: form.kart_aktif ? COLORS.green : "#94a3b8",
                    }}
                  />

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: COLORS.text,
                      }}
                    >
                      {form.kurum_adi || TT.pageTitle}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: COLORS.textSoft,
                      }}
                    >
                      {form.kart_aktif ? TT.active : TT.passive}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  type="submit"
                  disabled={saving}
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  variant="contained"
                  size="large"
                  sx={{
                    minHeight: 50,
                    px: {
                      xs: 2,
                      sm: 3.5,
                    },
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 900,
                    bgcolor: COLORS.navy,
                    boxShadow: "0 10px 26px rgba(8,26,50,.22)",
                    "&:hover": {
                      bgcolor: COLORS.navySoft,
                    },
                  }}
                >
                  {saving ? TT.saving : TT.save}
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </Box>

      <Snackbar
        open={copySnack}
        autoHideDuration={2200}
        onClose={() => setCopySnack(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setCopySnack(false)}
          sx={{ fontWeight: 800 }}
        >
          {TT.copied}
        </Alert>
      </Snackbar>
    </>
  );
}

export function IletisimPage(props) {
  return <SharedIletisimForm mode="list" {...props} />;
}

export function IletisimEdit(props) {
  return <SharedIletisimForm mode="edit" {...props} />;
}

export function IletisimCreate(props) {
  return <SharedIletisimForm mode="create" {...props} />;
}

export default IletisimPage;
